/**
 * Grid interaction logic: hit testing, scrolling, selection, and resize drags
 * (Requirements 1.5, 1.6, 1.7, 1.8).
 *
 * Pure functions over explicit state, with no DOM references — `src/ui/gridView.ts`
 * is the thin layer that turns real events into these calls. Two reasons that split
 * is worth it: the arithmetic of "which column border is under the cursor" is
 * exactly the kind of thing that is wrong by two pixels forever if it can only be
 * checked by hand, and it lets the whole interaction model be tested in Node.
 *
 * Scope note: this module covers pointer interaction plus the *scrolling* keys
 * (arrows, PageUp/PageDown, Home/End inside the sheet). The full Excel shortcut
 * table, `Ctrl+Arrow` block-edge navigation, and `F4` anchor cycling are task 12's
 * Shortcut_Handler, which will call into the same selection helpers here rather
 * than growing its own copy.
 */

import { normalizeRange, type Addr, type Range, type SheetId } from '../core/types.js';
import {
  clampScroll,
  COL_HEADER_HEIGHT_PX,
  indexAt,
  offsetOf,
  ROW_HEADER_WIDTH_PX,
  scrollToShow,
  sizeOf,
  type Axis,
} from './geometry.js';

/** Grab width of a header resize handle, in CSS pixels. */
export const RESIZE_HANDLE_PX = 4;

/** Side of the fill handle drawn at the selection's bottom-right corner, in CSS pixels. */
export const FILL_HANDLE_PX = 6;

/** Smallest width or height a drag may produce. */
export const MIN_SIZE_PX = 8;

/** Rows one wheel notch scrolls when the event reports lines rather than pixels. */
export const WHEEL_LINE_ROWS = 3;

/** What is under a point in the canvas. */
export type Hit =
  | { k: 'corner' }
  | { k: 'colHeader'; col: number }
  | { k: 'rowHeader'; row: number }
  | { k: 'colResize'; col: number }
  | { k: 'rowResize'; row: number }
  | { k: 'cell'; row: number; col: number };

/** The geometry a hit test needs: the axes, the scroll offsets, and the freeze. */
export interface HitContext {
  rowAxis: Axis;
  colAxis: Axis;
  scrollX: number;
  scrollY: number;
  freeze: Addr | null;
}

/** Pixel offset within the scrolling pane, freeze accounted for. */
function paneX(ctx: HitContext, x: number): number {
  const frozenCols = ctx.freeze?.col ?? 0;
  const frozenWidth = offsetOf(ctx.colAxis, frozenCols);
  const local = x - ROW_HEADER_WIDTH_PX;
  // Inside the frozen band the content is unscrolled, so the pane offset is the
  // pixel itself; past it the scroll offset applies.
  return local <= frozenWidth ? local : local + ctx.scrollX;
}

function paneY(ctx: HitContext, y: number): number {
  const frozenRows = ctx.freeze?.row ?? 0;
  const frozenHeight = offsetOf(ctx.rowAxis, frozenRows);
  const local = y - COL_HEADER_HEIGHT_PX;
  return local <= frozenHeight ? local : local + ctx.scrollY;
}

/**
 * True when `px` sits within the grab handle of the *trailing* edge of `index`.
 *
 * Excel's rule: the handle belongs to the column to the left of the border, which
 * is why a drag on the A|B border resizes A.
 */
function nearTrailingEdge(axis: Axis, index: number, px: number): boolean {
  const end = offsetOf(axis, index) + sizeOf(axis, index);
  return Math.abs(px - end) <= RESIZE_HANDLE_PX;
}

/** Classifies a canvas point (Requirements 1.6, 1.7). */
export function hitTest(ctx: HitContext, x: number, y: number): Hit {
  const inRowHeader = x < ROW_HEADER_WIDTH_PX;
  const inColHeader = y < COL_HEADER_HEIGHT_PX;
  if (inRowHeader && inColHeader) return { k: 'corner' };

  if (inColHeader) {
    const px = paneX(ctx, x);
    const col = indexAt(ctx.colAxis, px);
    if (nearTrailingEdge(ctx.colAxis, col, px)) return { k: 'colResize', col };
    // The leading edge of a column is the trailing edge of the one before it.
    if (col > 0 && Math.abs(px - offsetOf(ctx.colAxis, col)) <= RESIZE_HANDLE_PX) {
      return { k: 'colResize', col: col - 1 };
    }
    return { k: 'colHeader', col };
  }

  if (inRowHeader) {
    const px = paneY(ctx, y);
    const row = indexAt(ctx.rowAxis, px);
    if (nearTrailingEdge(ctx.rowAxis, row, px)) return { k: 'rowResize', row };
    if (row > 0 && Math.abs(px - offsetOf(ctx.rowAxis, row)) <= RESIZE_HANDLE_PX) {
      return { k: 'rowResize', row: row - 1 };
    }
    return { k: 'rowHeader', row };
  }

  return { k: 'cell', row: indexAt(ctx.rowAxis, paneY(ctx, y)), col: indexAt(ctx.colAxis, paneX(ctx, x)) };
}

/**
 * Canvas rectangle of the fill handle: the small square on the selection's
 * bottom-right corner (Requirement 12.9).
 *
 * Kept here rather than in the painter because both the painter and the pointer
 * handler need it, and a handle drawn somewhere other than where it can be grabbed is
 * the one bug this geometry can have. Frozen bands ignore the matching scroll offset,
 * exactly as the painter's cell rectangles do.
 */
export function fillHandleRect(
  ctx: HitContext,
  selection: Range,
): { x: number; y: number; w: number; h: number } {
  const s = normalizeRange(selection);
  const frozenRows = ctx.freeze?.row ?? 0;
  const frozenCols = ctx.freeze?.col ?? 0;
  const right =
    ROW_HEADER_WIDTH_PX +
    offsetOf(ctx.colAxis, s.c1) +
    sizeOf(ctx.colAxis, s.c1) -
    (s.c1 < frozenCols ? 0 : ctx.scrollX);
  const bottom =
    COL_HEADER_HEIGHT_PX +
    offsetOf(ctx.rowAxis, s.r1) +
    sizeOf(ctx.rowAxis, s.r1) -
    (s.r1 < frozenRows ? 0 : ctx.scrollY);
  return {
    x: right - FILL_HANDLE_PX / 2,
    y: bottom - FILL_HANDLE_PX / 2,
    w: FILL_HANDLE_PX,
    h: FILL_HANDLE_PX,
  };
}

/** True when a canvas point is on the selection's fill handle. */
export function hitFillHandle(ctx: HitContext, selection: Range, x: number, y: number): boolean {
  if (x < ROW_HEADER_WIDTH_PX || y < COL_HEADER_HEIGHT_PX) return false;
  const rect = fillHandleRect(ctx, selection);
  // One pixel of slack on each side: a 6-pixel target is small for a pointer, and
  // missing the handle silently starts a new selection instead.
  return (
    x >= rect.x - 1 && x <= rect.x + rect.w + 1 && y >= rect.y - 1 && y <= rect.y + rect.h + 1
  );
}

/** The cursor a hit implies, so the view has one place to read it from. */
export function cursorFor(hit: Hit): string {
  switch (hit.k) {
    case 'colResize':
      return 'col-resize';
    case 'rowResize':
      return 'row-resize';
    case 'colHeader':
    case 'rowHeader':
    case 'corner':
      return 'default';
    case 'cell':
      return 'cell';
  }
}

/* ---------------------------------------------------------------- selection */

/** Active cell plus the rectangle it anchors. */
export interface SelectionState {
  /** The cell the keyboard acts on and the formula bar shows. */
  active: Addr;
  /** The fixed corner of a drag or a `Shift`-extended selection. */
  anchor: Addr;
  /** The selected rectangle, always normalized. */
  range: Range;
}

/** A selection of one cell. */
export function selectCell(at: Addr): SelectionState {
  return { active: at, anchor: at, range: { sheet: at.sheet, r0: at.row, c0: at.col, r1: at.row, c1: at.col } };
}

/** Extends a selection to `to`, keeping the anchor and moving the active cell. */
export function extendTo(state: SelectionState, to: Addr): SelectionState {
  return {
    active: to,
    anchor: state.anchor,
    range: normalizeRange({
      sheet: to.sheet,
      r0: state.anchor.row,
      c0: state.anchor.col,
      r1: to.row,
      c1: to.col,
    }),
  };
}

/** Whole-column selection (`Ctrl+Space` in task 12, header click here). */
export function selectColumn(sheet: SheetId, col: number, lastRow: number): SelectionState {
  return {
    active: { sheet, row: 0, col },
    anchor: { sheet, row: 0, col },
    range: { sheet, r0: 0, c0: col, r1: lastRow, c1: col },
  };
}

/** Whole-row selection. */
export function selectRow(sheet: SheetId, row: number, lastCol: number): SelectionState {
  return {
    active: { sheet, row, col: 0 },
    anchor: { sheet, row, col: 0 },
    range: { sheet, r0: row, c0: 0, r1: row, c1: lastCol },
  };
}

/* ----------------------------------------------------------------- scrolling */

export interface ScrollState {
  x: number;
  y: number;
}

export interface PaneSize {
  widthPx: number;
  heightPx: number;
}

/**
 * Applies a wheel event's delta (Requirement 1.5).
 *
 * `deltaMode` follows the DOM: 0 pixels, 1 lines, 2 pages. Lines and pages are
 * converted through the *current* geometry rather than a fixed pixel constant, so a
 * notch scrolls three rows whatever those rows' heights are.
 */
export function scrollByWheel(
  scroll: ScrollState,
  rowAxis: Axis,
  colAxis: Axis,
  pane: PaneSize,
  delta: { x: number; y: number; mode: 0 | 1 | 2 },
): ScrollState {
  const rowPx = rowAxis.defaultPx;
  const colPx = colAxis.defaultPx;
  const scale =
    delta.mode === 1
      ? { x: colPx, y: rowPx * WHEEL_LINE_ROWS }
      : delta.mode === 2
        ? { x: pane.widthPx, y: pane.heightPx }
        : { x: 1, y: 1 };
  return clampScroll(
    { x: scroll.x + delta.x * scale.x, y: scroll.y + delta.y * scale.y },
    rowAxis,
    colAxis,
    pane,
  );
}

/** Keys this module handles. Everything else belongs to task 12. */
export type NavKey =
  | 'ArrowUp'
  | 'ArrowDown'
  | 'ArrowLeft'
  | 'ArrowRight'
  | 'PageUp'
  | 'PageDown'
  | 'Home'
  | 'End';

/** True for keys {@link navigate} understands. */
export function isNavKey(key: string): key is NavKey {
  return (
    key === 'ArrowUp' ||
    key === 'ArrowDown' ||
    key === 'ArrowLeft' ||
    key === 'ArrowRight' ||
    key === 'PageUp' ||
    key === 'PageDown' ||
    key === 'Home' ||
    key === 'End'
  );
}

export interface NavigateResult {
  selection: SelectionState;
  scroll: ScrollState;
}

/**
 * Moves or extends the selection by one key, then scrolls the minimum distance to
 * keep the new active cell visible.
 *
 * `End` here means "the last column of the used range on this row's axis", which is
 * the plain-key behaviour; `Ctrl+End` (Requirement 3.5) is task 12's.
 */
export function navigate(
  state: SelectionState,
  scroll: ScrollState,
  rowAxis: Axis,
  colAxis: Axis,
  pane: PaneSize,
  key: NavKey,
  opts: { extend?: boolean; usedRange?: Range } = {},
): NavigateResult {
  const from = state.active;
  const rowsPerPage = Math.max(1, Math.floor(pane.heightPx / Math.max(1, rowAxis.defaultPx)));
  const used = opts.usedRange;
  let row = from.row;
  let col = from.col;

  switch (key) {
    case 'ArrowUp':
      row -= 1;
      break;
    case 'ArrowDown':
      row += 1;
      break;
    case 'ArrowLeft':
      col -= 1;
      break;
    case 'ArrowRight':
      col += 1;
      break;
    case 'PageUp':
      row -= rowsPerPage;
      break;
    case 'PageDown':
      row += rowsPerPage;
      break;
    case 'Home':
      col = 0;
      break;
    case 'End':
      col = used === undefined ? col : Math.max(0, used.c1);
      break;
  }

  const to: Addr = {
    sheet: from.sheet,
    row: Math.max(0, Math.min(row, rowAxis.count - 1)),
    col: Math.max(0, Math.min(col, colAxis.count - 1)),
  };
  const selection = opts.extend === true ? extendTo(state, to) : selectCell(to);
  return {
    selection,
    scroll: scrollToShow(scroll, rowAxis, colAxis, pane, to.row, to.col),
  };
}

/**
 * Scroll offsets implied by a scrollbar thumb position.
 *
 * The grid cannot use a native overflow container: 5,000,000 rows at 20 px is
 * 100,000,000 px of content and browsers cap element height far below that. So the
 * scrollbars are proportional — the thumb position is a fraction of the track, and
 * the fraction maps onto the scrollable extent.
 */
export function scrollFromThumb(
  fraction: number,
  axisTotalPx: number,
  paneExtentPx: number,
): number {
  const max = Math.max(0, axisTotalPx - paneExtentPx);
  return Math.min(max, Math.max(0, fraction) * max);
}

/** Thumb position as a fraction of the track, for rendering the scrollbar. */
export function thumbFraction(scrollPx: number, axisTotalPx: number, paneExtentPx: number): number {
  const max = Math.max(0, axisTotalPx - paneExtentPx);
  if (max === 0) return 0;
  return Math.min(1, Math.max(0, scrollPx / max));
}

/* ------------------------------------------------------------ resize drags */

/** An in-progress column or row resize (Requirements 1.6, 1.7). */
export interface ResizeDrag {
  kind: 'col' | 'row';
  index: number;
  /** Pointer position where the drag started, in canvas pixels. */
  startPx: number;
  /** Size of the target when the drag started. */
  startSizePx: number;
}

export function beginResize(
  hit: Extract<Hit, { k: 'colResize' } | { k: 'rowResize' }>,
  axis: Axis,
  pointerPx: number,
): ResizeDrag {
  const index = hit.k === 'colResize' ? hit.col : hit.row;
  return {
    kind: hit.k === 'colResize' ? 'col' : 'row',
    index,
    startPx: pointerPx,
    startSizePx: sizeOf(axis, index),
  };
}

/** The size a drag has reached, floored at {@link MIN_SIZE_PX}. */
export function resizeSize(drag: ResizeDrag, pointerPx: number): number {
  return Math.max(MIN_SIZE_PX, Math.round(drag.startSizePx + (pointerPx - drag.startPx)));
}
